/**
 * Translation keys (register in backend / CMS):
 * - Source-data tags / column: `component/object/translations.md`
 * - Pills, panel chrome, confirm / cancel-pending-restore: `component/override/translations.md`
 * - view.objectdetail.sourcedata.column.master-data-source  (e.g. displayname: {{importformat}})
 * - object.sourcedata.directfield.edit.warning  (see component/object/dialog/objectfield.edit)
 */
import {
    IKnockoutComponent,
    IObjectData,
    IOverride,
    IRouteDefinitionParams,
    IRule,
    IRuleset,
    ISyncField,
    TBaseOrObservableType
} from "../../index";
import ObjectDetails from "./ObjectDetails";
import {_} from 'lodash';
import {convertFromServerString, convertToServerString, validateServerDate} from "../../util/datetime";
import {storeObservable} from "../../util/sessionstore";
import {
    createErrorMessage,
    getObjectDefinition,
    getObjectDisplayName,
    getRawSyncFieldValue,
    getSourceTranslation,
    getTelLink,
    IObjectDefinition,
    isNullOrUndefined
} from "../../util/misc";
import {ObjectTypeId, SNACKBAR_DURATION_LONG, SPINNER} from "../../constants";
import {global, importFormatManager, lm} from "../../appContext";
import {nsRuleset} from "../ruleset/nsRuleset";
import {nsFa} from "../fa/fn";
import {nsSyncFields} from "../../req/nsSyncFields";
import {IRequestObject} from "../../req/index";
import pubsub from "../../pubsub";
import {nsOverride} from "../override/nsOverride";
import {EMPTY_STRING, REQUEST_MESSAGE} from "../../constants/Language";
import * as ko from "knockout";
import {nsTooltip} from "../../util/nsTooltip";
import PromiseRequest from "../../req/PromiseRequest";
import {ISyncfieldListParent, SyncfieldRowVm} from "./syncfieldRow";
import {moment} from 'moment';
import i = nsFa.i;
import getSyncFieldOverride = nsOverride.getSyncFieldOverride;
import isOverridePendingDeletion = nsOverride.isOverridePendingDeletion;
import requestRestoreOverride = nsOverride.requestRestoreOverride;
import rawHtml from './raw.html?raw';

export let template: string = rawHtml;

export class viewModel extends ObjectDetails implements ISyncfieldListParent {

    private allSyncFields: ISyncField[] = [];
    private objDef: IObjectDefinition;
    private objectTypeId: ObjectTypeId;
    private readonly asc: KnockoutObservable<boolean> = storeObservable("syncfields.sort.asc", true);
    private readonly pv = global.permissionValidator;
    private readonly req_deleteoverride = global.getPromiseRequest('deleteoverride');
    private readonly reqRestoreOverride = new PromiseRequest('restoreoverride');
    private readonly reqUpdateOverrideComment = new PromiseRequest('updateoverridecomment');
    private readonly sortcol: KnockoutObservable<string> = storeObservable("syncfields.sort.col", '$sourceName');
    private readonly toggleAll: KnockoutObservable<boolean> = storeObservable("syncfields.toggle.show.all", false);
    private reqobj_getsyncfields!: IRequestObject;
    private toggleOverride: KnockoutObservable<boolean> = storeObservable("syncfields.toggle.override", true);
    private readonly rowByKey = new Map<string, SyncfieldRowVm>();
    private readonly toggleOverrideSub: KnockoutSubscription;
    enableDerivedValue: boolean = this.pv.isMinSuperManager();
    protected enableOverride: boolean = true;

    searchkey: KnockoutObservable<string> = ko.observable('');

    /** Heading badge mirrors the visible syncfield list. */
    private readonly searchBadge = ko.pureComputed((): number | null => {
        if (!this.isReady()) {
            return null;
        }
        return this.syncfields().length;
    });

    /** Spinner on first load only (client-side filter is instant thereafter). */
    private readonly searchBadgeWaiting = ko.pureComputed((): boolean => !this.isReady());

    private authDataSource: number | null = null;

    /** Sorted syncfields wrapped as expandable row VMs (expand state preserved by key). */
    readonly syncfieldRows = ko.pureComputed((): SyncfieldRowVm[] => {
        const fields = this.syncfields();
        const rows: SyncfieldRowVm[] = [];
        const activeKeys = new Set<string>();

        for (const sf of fields) {
            const key = this.rowKey(sf);
            activeKeys.add(key);
            let row = this.rowByKey.get(key);
            if (!row) {
                row = new SyncfieldRowVm(sf, this);
                this.rowByKey.set(key, row);
            } else {
                row.setSyncfield(sf);
            }
            rows.push(row);
        }

        for (const key of Array.from(this.rowByKey.keys())) {
            if (!activeKeys.has(key)) {
                this.rowByKey.delete(key);
            }
        }

        return rows;
    });

    /** CSS grid tracks for the current column set (override toggle / derived / func). */
    private readonly gridStyle = ko.pureComputed((): string => {
        const areas: string[] = [];
        const cols: string[] = [];
        if (this.showOverrideColumn()) {
            areas.push('expand');
            cols.push('max-content');
        }
        areas.push('sourceName', 'eadm', 'value');
        cols.push('minmax(2em, 1fr)', 'minmax(2em, 1fr)', 'minmax(2em, 1.5fr)');
        if (this.enableDerivedValue) {
            areas.push('derived');
            cols.push('minmax(2em, 1.5fr)');
        }
        if (this.showOverrideColumn()) {
            areas.push('status');
            cols.push('minmax(6em, max-content)');
        }
        if (this.showFuncColumn()) {
            areas.push('func');
            cols.push('max-content');
        }
        return [
            `grid-template-areas: '${areas.join(' ')}'`,
            `grid-template-columns: ${cols.join(' ')}`,
            `--column-count: ${areas.length}`,
        ].join('; ');
    });

    constructor(params: IRouteDefinitionParams) {
        super(params, false);
        this.objectTypeId = this.__routeDef.objectTypeId!;
        this.enableOverride = this.enableOverride && !this.__routeDef.manual && [ObjectTypeId.User, ObjectTypeId.Department, ObjectTypeId.Group].includes(this.objectTypeId);
        this.objDef = getObjectDefinition(this.objectTypeId)!;
        this.toggleOverrideSub = this.toggleOverride.subscribe((enabled) => {
            if (!enabled) {
                this.collapseAllRows();
            }
        });
        pubsub.on(this, 'object.needs.refresh', () => this.request());
        this.request();
    }

    showOverrideColumn(): boolean {
        return this.enableOverride && this.toggleOverride();
    }

    showFuncColumn(): boolean {
        this.isReady();
        if (!(this.pv.isMinCompanyAdmin() || this.isAtLeastOneSyncfieldOverrideable())) {
            return false;
        }
        // Override row actions follow the override toggle; direct field edit does not.
        return this.showOverrideColumn()
            || this.allSyncFields.some(sf => this.isDirectlyEditable(sf));
    }

    protected override request(): Promise<void> {
        this.messageData({message: REQUEST_MESSAGE});
        this.isReady(false);

        const routeRequest = global.router.requestRouteData ?? this.__requestRouteData;
        if (!routeRequest) {
            return Promise.resolve();
        }
        return routeRequest
            .request()
            .then(async (response: any[]) => {
                await this.onSuccess(response as [IObjectData]);
                const objectData = this.data();
                if (!objectData) {
                    return;
                }
                const _authDataSource = getRawSyncFieldValue('AuthDataSource', objectData.syncfields)
                this.authDataSource = isNullOrUndefined(_authDataSource)
                    ? null
                    : Number(_authDataSource)
                this.reqobj_getsyncfields = nsSyncFields.getSyncFields(this.objectTypeId, null, this.authDataSource ?? undefined);
                return this.reqobj_getsyncfields.request
                    .then((allSyncfields: ISyncField[]) => {
                        this.allSyncFields = allSyncfields;
                        objectData.syncfields.forEach((item: ISyncField) => {
                            const syncfieldTemplate = allSyncfields.find((sf: ISyncField) => sf.name == item.name) ?? item;
                            item.displayname = item.displayname || syncfieldTemplate.displayname;
                            item.description = item.description || syncfieldTemplate.description;
                            // bugfix: there seems to be an API bug when the object is a manual object - then all ids are 0.
                            item.syncfieldid = syncfieldTemplate.syncfieldid
                            item.objecttypeid = syncfieldTemplate.objecttypeid
                        })
                    })
            })
            .then(() => {
                this.messageData(null);
                this.isReady(true);
            })
            .catch(this.onError.bind(this))
    }

    override onError(reason: string) {
        this.messageData(createErrorMessage(String(reason)))
        // A failed refetch must not hide source data that already loaded.
        if (this.data()) {
            this.isReady(true);
        }
    }

    syncfields: KnockoutComputed<ISyncField[]> = ko.pureComputed((): ISyncField[] => {
        const col = this.sortcol() === '$name' ? '$sourceName' : this.sortcol();
        const asc = this.asc();
        const ret = _(this.syncfieldsFilter())
            .sortBy(
                (item: ISyncField) => {
                    switch (col) {
                        case '$sourceName':
                            return this.getSourceSystemName(item)?.toLowerCase();
                        case '$eadmName':
                            return this.getEadmName(item)?.toLowerCase();
                        case 'override':
                            const override = this.getOverride(item);
                            if (!override) {
                                return 0;
                            }
                            return convertFromServerString(override.overriddenwhen || override.deletedwhen).unix();
                        case 'required':
                            return item.required ? 0 : 1;
                        default:
                            return String((item as any)[col]).toLowerCase();
                    }
                },
                (item: ISyncField) => this.getEadmName(item)?.toLowerCase() ?? '',
            )
            .value() as ISyncField[];

        if (asc) {
            return ret;
        } else {
            return ret.reverse();
        }
    });

    protected syncfieldsFilter(): ISyncField[] {
        const objectData = this.data();
        if (!objectData?.syncfields) {
            return [];
        }
        let ret: ISyncField[];
        if (this.toggleAll()) {
            ret = this.allSyncFields.reduce((all: ISyncField[], sf: ISyncField) => {
                if (all.find(o => o.name == sf.name)) {
                    return all;
                }
                return [...all, sf];
            }, objectData.syncfields);
        } else {
            ret = objectData.syncfields.filter(nsSyncFields.hasValue);
        }
        return ret
            .filter((item: ISyncField) => {
                const sk = this.searchkey()?.toLowerCase();
                if (!sk) {
                    return true;
                }
                return this.getSourceSystemName(item)?.toLowerCase().indexOf(sk) != -1
                    || this.getEadmName(item)?.toLowerCase().indexOf(sk) != -1
                    || (this.getValue(item)?.toLowerCase() ?? '').indexOf(sk) != -1;
            });
    };

    hasValue = nsSyncFields.hasValue;

    getValue(item: ISyncField): string {
        // trigger function whenever the language changes
        lm.selectedLanguage()
        const value = item.value;
        if (value === "") {
            return lm.t(EMPTY_STRING) ?? '';
        }
        switch (item.name) {
            case 'UserPassword':
            case "Socialsecuritynumber":
                return value ? `[${lm.t('not visible') ?? ''}]` : String(value);
            default:
                return String(value);
        }
    }

    prepareValues(item: ISyncField): ISyncField[] {
        switch (item.name) {
            case 'WwwUrl':
                const ret = item.value.split('\\').map((val: string) => {
                    const ret = _.cloneDeep(item);
                    ret.value = val.split(' ')[0];
                    return ret;
                }).filter(nsSyncFields.hasValue);
                return ret;
            case "APositionManager":
                return nsSyncFields.getArrayByString(item.value).map((val: string) => {
                    const ret = _.cloneDeep(item);
                    ret.value = val;
                    return ret;
                }).filter(nsSyncFields.hasValue);
            default:
                return nsSyncFields.getArrayByString(item.value).map((val: string) => {
                    const ret = _.cloneDeep(item);
                    ret.value = val;
                    return ret;
                }).filter(nsSyncFields.hasValue);
        }
    }

    validateServerDate = validateServerDate

    getDerivedValue(item: ISyncField): string | null {
        const val = this.toggleOverride?.() && this.getOverride(item)?.value || item.value;
        if (!val) {
            return null;
        }
        switch (item.name) {
            case 'AuthDataSource': {
                const authDataSource = item.value;
                return importFormatManager.getImportFormatById(Number(authDataSource))?.name ?? ''
            }
            case "BusinessNumber":
                return `${i('fa-external-link')} <a href="https://w2.brreg.no/enhet/sok/detalj.jsp?orgnr=${val}" title="${lm.t('see key information from CCR') ?? ''}" target="_blank">${lm.t('CCR') ?? ''}</a>`;
            case "Email":
                return `${i('fa-envelope')} <a href="mailto:${val}">${lm.t('send email') ?? ''}</a>`;
            case "Mobile":
                return getTelLink(val, 'fa-mobile');
            case "PrivatePhone":
            case "TelephoneNumber":
            case "WorkPhone":
                return getTelLink(val);
            case "ObjectTypeId":
                return lm.t(this.objDef.name) ?? '';
            case "SourceId": // group
            case "Parent":
            case "DepartmentNumber": // department
            case 'ParentDepartmentNumber':
            case 'WwwUrl':
                if (val === "0" || val === "Root") {
                    return lm.t('root');
                }
                return null;
        }
        return null;
    }

    getDerivedValueComponent(item: ISyncField): IKnockoutComponent | undefined {
        const objectData = this.data();
        if (!objectData) {
            return undefined;
        }
        return nsSyncFields.getDerivedValueComponent(item, {
            objectTypeId: this.objectTypeId,
            linkable: this.enableDerivedValue,
            object: objectData,
        }) ?? undefined;
    }

    // produces a ruleset with the same attribute, operator and value as in given sync field
    // this can be used both in searchobject and when populating rules for a new ruleset
    private getRulesetObject(sf: ISyncField): IRuleset {
        let valueDisplayName = sf.value === '' ? lm.t(EMPTY_STRING) : sf.value;
        const objectData = this.data()!;

        const rule: IRule = nsRuleset.generateRule({
            argument: sf.value,
            argumentoperator: 'Er lik',
            attribute: sf.name,
        });

        if (sf.value.indexOf('|') > -1) {
            const itemValues = sf.value.split('|').filter(Boolean);
            if (itemValues.length == 1) {
                rule.argumentoperator = 'Inneholder';
                rule.argument = itemValues[0] ?? '';
                valueDisplayName = rule.argument ?? null;
            }
            if (itemValues.length > 1) {
                rule.argumentoperator = 'Inneholder en av';
                rule.argument = itemValues.join(',');
                valueDisplayName = itemValues.join('/')
            }
        }

        const name = lm.t('user|p') + ` [${sf.name} ⇨ ${valueDisplayName}]`;

        const ruleset: IRuleset = nsRuleset.generateRuleset({
            companyid: objectData.orgnr,
            name: name,
            objecttype: objectData.type,
            rules: [rule],
        })

        return ruleset;
    }

    getOverride(sf: ISyncField): IOverride | undefined {
        const objectData = this.data();
        if (!objectData) {
            return undefined;
        }
        return getSyncFieldOverride(objectData, sf) ?? undefined;
    }

    private getOverrides(): number {
        return this.syncfields()?.filter(sf => this.getOverride(sf)).length;
    }

    onEditOverrideClick(syncfield: ISyncField) {
        this.onMutableOverrideClick(syncfield, `${i('fa-pencil')} ${lm.t('edit value')} [${this.getSourceSystemName(syncfield)}]`)
    }

    private onMutableOverrideClick(syncfield: ISyncField, title: TBaseOrObservableType<string>) {
        const objectData = this.data();
        if (!objectData) {
            return;
        }
        global.dialogManager.addComponent({
            title: title,
            fullWidth: false,
            content: {
                name: 'component/object/dialog/syncfield.edit',
                params: {
                    object: objectData,
                    syncfield: syncfield,
                    onCreated: (created: IOverride, replacedOverrideId?: number) => {
                        this.applyCreatedOverride(created, replacedOverrideId);
                    },
                },
            },
        });
    }

    onDeleteOverrideClick(syncfield: ISyncField) {
        const override = this.getOverride(syncfield);
        if (!override || override.deletedbyid) {
            return;
        }
        const sourceName = this.getSourceSystemName(syncfield);
        global.dialogManager.addConfirm({
            title: `${i('fa-exclamation-triangle')} ((view.objectdetail.sourcedata.confirm.restore.title))`,
            content: `<p>${lm.t({
                k: 'view.objectdetail.sourcedata.confirm.restore',
                r: {name: sourceName},
            })}</p>`,
            buttons: {
                cancel: {
                    label: 'cancel',
                },
                confirm: {
                    label: 'restore',
                    onClick: () => {
                        this.messageData({message: `((deleting|td)) ${SPINNER}`});
                        this.req_deleteoverride
                            .request({
                                id: override.id,
                            })
                            .then(() => {
                                this.invalidateOverrideCaches();
                                // deleteoverride returns no payload — mirror BE physical vs soft-delete.
                                this.applyDeletedOverride(override);
                                global.snackbarData({
                                    message: 'restore completed',
                                    duration: SNACKBAR_DURATION_LONG
                                });
                                this.messageData(null);
                            })
                            .catch((reason: string) => {
                                this.messageData(createErrorMessage(String(reason)));
                            })
                    },
                }
            }
        });
    }

    /** Abort pending restore (`will restore`) via `restoreoverride` — keep the override. */
    onCancelPendingRestoreClick(syncfield: ISyncField) {
        const override = this.getOverride(syncfield);
        if (!override || !isOverridePendingDeletion(override)) {
            return;
        }
        const sourceName = this.getSourceSystemName(syncfield);
        global.dialogManager.addConfirm({
            title: `${i('fa-exclamation-triangle')} ((view.objectdetail.sourcedata.confirm.cancel.pending.restore.title))`,
            content: `<p>${lm.t({
                k: 'view.objectdetail.sourcedata.confirm.cancel.pending.restore',
                r: {name: sourceName},
            })}</p>`,
            buttons: {
                cancel: {
                    label: 'cancel',
                },
                confirm: {
                    label: 'override.btn.keep.override',
                    onClick: () => {
                        this.messageData({message: `((updating|td)) ${SPINNER}`});
                        requestRestoreOverride(this.reqRestoreOverride, override)
                            .then(() => {
                                this.invalidateOverrideCaches();
                                // restoreoverride returns no payload — reload overrides (real sync id).
                                return this.refreshObjectData();
                            })
                            .then(() => {
                                global.snackbarData({
                                    message: 'view.objectdetail.sourcedata.snackbar.cancel.pending.restore',
                                    duration: SNACKBAR_DURATION_LONG
                                });
                                this.messageData(null);
                            })
                            .catch((reason: string) => {
                                this.messageData(createErrorMessage(String(reason)));
                            })
                    },
                }
            }
        });
    }

    private invalidateOverrideCaches(): void {
        global.cacheManager.removeCache('getruleset');
        global.cacheManager.removeCache('searchruleset');
        global.cacheManager.removeCache('searchobject');
        global.cacheManager.removeCache('getobject');
    }

    /** Persists override comment via `updateoverridecomment` and patches the in-memory override. */
    updateOverrideComment(overrideId: number, comment: string): Promise<void> {
        return this.reqUpdateOverrideComment
            .request({id: overrideId, comment})
            .then(() => {
                global.cacheManager.removeCache('getobject');
                global.cacheManager.removeCache('searchobject');
                this.applyOverrideComment(overrideId, comment);
            })
            .catch((reason: string) => {
                this.messageData(createErrorMessage(String(reason)));
                return Promise.reject(reason);
            });
    }

    /** Insert/replace a syncfield override from `createoverride` response (no full reload). */
    private applyCreatedOverride(created: IOverride, replacedOverrideId?: number): void {
        const objectData = this.data();
        if (!objectData) {
            return;
        }
        const overrides = (objectData.overrides ?? [])
            .filter(o => o.id != replacedOverrideId && o.id != created.id);
        overrides.push(created);
        objectData.overrides = overrides;
        this.notifyObjectOverridesChanged();
        const row = this.rowByKey.get(this.overrideRowKey(created));
        row?.bumpOverridePanelRevision();
    }

    /**
     * Mirror `deleteoverride`: unsynced active → remove row; otherwise soft-delete locally.
     * API returns no override payload.
     */
    private applyDeletedOverride(override: IOverride): void {
        const objectData = this.data();
        if (!objectData?.overrides) {
            return;
        }
        const isPhysicalDelete = !override.syncronizedimportfileid && !override.deletedbyid;
        if (isPhysicalDelete) {
            objectData.overrides = objectData.overrides.filter(o => o.id !== override.id);
            this.rowByKey.get(this.overrideRowKey(override))?.collapse();
        } else {
            const user = global.login.user();
            objectData.overrides = objectData.overrides.map(o => {
                if (o.id !== override.id) {
                    return o;
                }
                return {
                    ...o,
                    syncronizedimportfileid: 0,
                    deletedbyid: user?.id ?? 0,
                    deletedby: getObjectDisplayName(user ?? undefined) ?? '',
                    deletedwhen: convertToServerString(moment()) ?? '',
                };
            });
        }
        this.notifyObjectOverridesChanged();
        this.rowByKey.get(this.overrideRowKey(override))?.bumpOverridePanelRevision();
    }

    /**
     * Re-fetch getobject (and re-align syncfield templates) without toggling isReady,
     * so expanded override panels keep state while sync ids update from BE.
     */
    private refreshObjectData(): Promise<void> {
        const routeRequest = global.router.requestRouteData ?? this.__requestRouteData;
        if (!routeRequest) {
            return Promise.resolve();
        }
        return routeRequest
            .request()
            .then(async (response: any[]) => {
                await this.onSuccess(response as [IObjectData]);
                const objectData = this.data();
                if (!objectData) {
                    return;
                }
                objectData.syncfields.forEach((item: ISyncField) => {
                    const syncfieldTemplate = this.allSyncFields.find((sf: ISyncField) => sf.name == item.name) ?? item;
                    item.displayname = item.displayname || syncfieldTemplate.displayname;
                    item.description = item.description || syncfieldTemplate.description;
                    item.syncfieldid = syncfieldTemplate.syncfieldid;
                    item.objecttypeid = syncfieldTemplate.objecttypeid;
                });
            });
    }

    private applyOverrideComment(overrideId: number, comment: string): void {
        const objectData = this.data();
        if (!objectData?.overrides) {
            return;
        }
        const override = objectData.overrides.find(o => o.id === overrideId);
        if (!override) {
            return;
        }
        override.comment = comment;
        this.notifyObjectOverridesChanged();
        this.rowByKey.get(this.overrideRowKey(override))?.bumpOverridePanelRevision();
    }

    /** Notify list/pill bindings that `object.overrides` changed in place. */
    private notifyObjectOverridesChanged(): void {
        this.data.valueHasMutated?.();
    }

    private overrideRowKey(override: IOverride): string {
        return override.targetobjectid ? String(override.targetobjectid) : override.targetname;
    }

    getSourceSystemName(item: ISyncField): string {
        return getSourceTranslation(item, true);
    }

    getEadmName(item: ISyncField): string {
        return getSourceTranslation(item, false);
    }

    private translateTableHeader = (key: string): string => {
        if (key === '$sourceName') {
            const importformat = this.authDataSource
                ? importFormatManager.getImportFormatById(this.authDataSource)?.name ?? ''
                : global.login.selectedOrganization()?.importformat?.name ?? '';
            return lm.t({
                k: 'view.objectdetail.sourcedata.column.master-data-source',
                r: {importformat},
            }) ?? '';
        }
        return lm.t(key) ?? '';
    };

    getSourceTranslation_tooltip(item: ISyncField) {
        // trigger function whenever the language changes
        lm.selectedLanguage()
        return lm.t(nsTooltip.getTitle_syncfield(item) ?? '') ?? ''
    }

    showOverrideButtons(syncfield: ISyncField) {
        if (syncfield.name === 'ExtendLife') {
            return false;
        }
        const isOverridden = !!this.getOverride(syncfield)
        const isOverrideable = this.isSyncfieldOverrideable(syncfield);
        return isOverridden || isOverrideable
    }

    isSyncfieldOverrideable(syncfield: ISyncField) {
        if (!this.enableOverride) {
            return false
        }
        return nsOverride.isSyncfieldOverrideable(syncfield)
    }

    // Not synced, so overrides don't apply; written straight to the object by `updateobjectfield`.
    isDirectlyEditable(syncfield: ISyncField): boolean {
        return this.enableOverride
            && this.objectTypeId === ObjectTypeId.User
            && ['UserName', 'Upn', 'Email'].includes(syncfield.name)
            && !syncfield.issynced
            && this.pv.isMinOrganizationAdmin();
    }

    onDirectEditClick(syncfield: ISyncField) {
        const objectData = this.data();
        if (!objectData) {
            return;
        }
        global.dialogManager.addComponent({
            title: `${i('fa-pencil')} ${lm.t('edit value')} [${this.getEadmName(syncfield)}]`,
            fullWidth: false,
            content: {
                name: 'component/object/dialog/objectfield.edit',
                params: {object: objectData, syncfield: syncfield},
            },
        });
    }

    private isAtLeastOneSyncfieldOverrideable(): boolean {
        return this.allSyncFields.some(sf => this.isSyncfieldOverrideable(sf) || this.isDirectlyEditable(sf));
    }

    private rowKey(sf: ISyncField): string {
        return sf.syncfieldid ? String(sf.syncfieldid) : sf.name;
    }

    private collapseAllRows(): void {
        for (const row of this.rowByKey.values()) {
            row.collapse();
        }
    }

    override dispose() {
        super.dispose();
        this.toggleOverrideSub.dispose();
        this.syncfieldRows.dispose();
        this.gridStyle.dispose();
        this.rowByKey.clear();
        pubsub.off(this, 'object.needs.refresh');
        this.reqobj_getsyncfields?.promise?.abort()
        this.req_deleteoverride.abort?.()
        this.reqRestoreOverride.abort?.()
        this.reqUpdateOverrideComment.abort?.()
    }

}
